import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatures } from '../contexts/FeatureContext';
import { 
  Search, 
  X, 
  User, 
  CreditCard, 
  Users, 
  ShoppingBag, 
  TrendingUp, 
  MapPin,
  UserCheck,
  Loader2
} from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map search result categories to feature IDs
const CATEGORY_FEATURE_MAP = {
  'groups': 'groups',
  'products': 'marketplace',
  'prediction': 'predictions',
  'agent': 'agent_network',
};

const categoryIcons = {
  user: User,
  contact: UserCheck,
  transaction: CreditCard,
  group: Users,
  product: ShoppingBag,
  prediction: TrendingUp,
  agent: MapPin,
};

const categoryColors = {
  user: 'text-blue-600 bg-blue-50 dark:bg-blue-950',
  contact: 'text-green-600 bg-green-50 dark:bg-green-950',
  transaction: 'text-purple-600 bg-purple-50 dark:bg-purple-950',
  group: 'text-orange-600 bg-orange-50 dark:bg-orange-950',
  product: 'text-pink-600 bg-pink-50 dark:bg-pink-950',
  prediction: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-950',
  agent: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950',
};

const categoryLabels = {
  users: 'People',
  contacts: 'Contacts',
  transactions: 'Transactions',
  groups: 'Groups',
  products: 'Products',
  predictions: 'Predictions',
  agents: 'Agents',
};

export const GlobalSearch = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ categories: {}, results: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { isFeatureEnabled } = useFeatures();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setResults({ categories: {}, results: [], total: 0 });
      return;
    }

    const delayDebounce = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [query]);

  const performSearch = async (searchQuery) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/search?q=${encodeURIComponent(searchQuery)}&category=all&limit=5`,
        {
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Filter results by feature availability
        const filteredResults = (data.results || []).filter(result => {
          const featureId = CATEGORY_FEATURE_MAP[result.type];
          // If no feature mapping, allow (e.g., users, transactions always visible)
          if (!featureId) return true;
          // Check if feature is enabled
          return isFeatureEnabled(featureId);
        });

        // Rebuild categories from filtered results
        const filteredCategories = {};
        filteredResults.forEach(result => {
          const category = result.type + 's'; // e.g., 'group' -> 'groups'
          if (!filteredCategories[category]) {
            filteredCategories[category] = [];
          }
          filteredCategories[category].push(result);
        });

        setResults({
          categories: filteredCategories,
          results: filteredResults,
          total: filteredResults.length
        });
        setSelectedIndex(0);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (result) => {
    navigate(result.url);
    onClose();
  };

  const handleKeyDown = (e) => {
    const flatResults = results.results || [];
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatResults[selectedIndex]) {
      e.preventDefault();
      handleResultClick(flatResults[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const ResultItem = ({ result, index, isSelected }) => {
    const Icon = categoryIcons[result.type] || Search;
    const colorClass = categoryColors[result.type] || 'text-gray-600 bg-gray-50';

    return (
      <button
        onClick={() => handleResultClick(result)}
        className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
          isSelected ? 'bg-blue-50 dark:bg-blue-950' : 'hover:bg-gray-50 dark:hover:bg-gray-900'
        }`}
        data-testid={`search-result-${index}`}
      >
        <div className={`p-2 rounded-lg ${colorClass}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {result.title}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
            {result.subtitle}
          </p>
        </div>
        {result.amount && (
          <div className="text-right">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {result.amount} {result.currency}
            </p>
          </div>
        )}
      </button>
    );
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20 px-4"
      onClick={onClose}
    >
      <Card 
        className="w-full max-w-2xl max-h-[600px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-800">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              ref={inputRef}
              type="text"
              placeholder="Search everything... (people, transactions, groups, products)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-10 pr-10 h-12 text-base"
              data-testid="global-search-input"
            />
            {loading && (
              <Loader2 className="absolute right-10 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 animate-spin" />
            )}
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 transform -translate-y-1/2"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex items-center gap-2 mt-3 text-xs text-gray-600 dark:text-gray-400">
            <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded">↑↓</kbd>
            <span>Navigate</span>
            <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded">Enter</kbd>
            <span>Select</span>
            <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded">Esc</kbd>
            <span>Close</span>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[480px]">
          {query.length < 2 ? (
            <div className="p-8 text-center text-gray-600 dark:text-gray-400">
              <Search className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Type at least 2 characters to search</p>
              <p className="text-xs mt-2">Search across people, transactions, groups, products, and more</p>
            </div>
          ) : loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 mx-auto animate-spin text-blue-600" />
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">Searching...</p>
            </div>
          ) : results.total === 0 ? (
            <div className="p-8 text-center text-gray-600 dark:text-gray-400">
              <Search className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No results found for "{query}"</p>
              <p className="text-xs mt-2">Try different keywords or check your spelling</p>
            </div>
          ) : (
            <div className="p-4 space-y-6">
              {Object.entries(results.categories).map(([category, items]) => {
                if (!items || items.length === 0) return null;
                
                return (
                  <div key={category}>
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2 px-3">
                      {categoryLabels[category] || category} ({items.length})
                    </h3>
                    <div className="space-y-1">
                      {items.map((result, idx) => {
                        const globalIndex = results.results.indexOf(result);
                        return (
                          <ResultItem
                            key={`${result.type}-${result.id}-${idx}`}
                            result={result}
                            index={globalIndex}
                            isSelected={globalIndex === selectedIndex}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {results.total > 0 && (
          <div className="p-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 text-center">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Showing {results.total} result{results.total !== 1 ? 's' : ''} for "{query}"
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};
